import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENTS = [
  { value: "lead", label: "Registration (Lead)" },
  { value: "sale", label: "Sale (Deposit)" },
  { value: "redep", label: "Redep (Repeat Deposit)" },
  { value: "revshare", label: "RevShare" },
  { value: "trash", label: "Trash (Reject Sale)" },
];

export function BulkPostbackDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const opts = useOptions();
  const [text, setText] = useState("");
  const [status, setStatus] = useState("sale");
  const [payout, setPayout] = useState("");
  const [revenue, setRevenue] = useState("");
  const [pct, setPct] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bonus, setBonus] = useState(false);
  const [buyerId, setBuyerId] = useState("");
  const [streamId, setStreamId] = useState("");
  const [qty, setQty] = useState("");
  const [sendPostback, setSendPostback] = useState(false);

  const buyerOpts = userOptions(opts.users);
  const { data: streamsData } = useQuery({
    queryKey: ["bonus-streams", buyerId],
    queryFn: async () => (await http.get(`/streams?buyer_id=${buyerId}&limit=1000`)).data,
    enabled: open && bonus && !!buyerId,
  });
  const streamOptions = (streamsData?.items || []).map((s) => ({
    value: s.id,
    label: `#${s.tenant_seq} — ${s.name || "Stream"}${s.offer_name && s.offer_name !== "—" ? ` · ${s.offer_name}` : ""}`,
    search: `#${s.tenant_seq} ${s.tenant_seq} ${s.name || ""} ${s.stream_key || ""} ${s.offer_name || ""}`,
  }));
  const qtyNum = parseInt(qty, 10) || 0;

  const isRS = status === "revshare";
  const rsPayout = isRS && revenue !== "" && pct !== ""
    ? Math.round((Number(revenue) || 0) * (Number(pct) || 0)) / 100 * 100 / 100
    : null;
  const rsPayoutValue = isRS ? Math.round(((Number(revenue) || 0) * (Number(pct) || 0)) ) / 100 : null;

  const ids = text.split("\n").map((s) => s.trim()).filter(Boolean);
  const uniqueIds = [...new Set(ids)];

  const reset = () => { setText(""); setPreview(null); setResult(null); setPayout(""); setRevenue(""); setPct(""); setStatus("sale"); setBonus(false); setBuyerId(""); setStreamId(""); setQty(""); setSendPostback(false); };

  const toggleBonus = (v) => {
    setBonus(!!v); setPreview(null); setResult(null);
    if (v && status === "trash") setStatus("sale");
  };

  const runPreview = async () => {
    if (uniqueIds.length === 0) return toast.error("Paste at least one Click ID");
    setBusy(true);
    try {
      const { data } = await http.post("/conversions/bulk-postback", { click_ids: uniqueIds, dry_run: true });
      setPreview(data); setResult(null);
    } catch (e) { toast.error("Preview failed", { description: e?.response?.data?.detail || "Try again." }); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (bonus) {
      if (!buyerId) return toast.error("Select a Buyer");
      if (!streamId) return toast.error("Select a Stream");
      if (qtyNum < 1 || qtyNum > 100) return toast.error("Quantity must be between 1 and 100");
    } else if (uniqueIds.length === 0) return toast.error("Paste at least one Click ID");
    if (!status) return toast.error("Select an event template");
    if (isRS && (revenue === "" || !Number.isFinite(Number(revenue)))) return toast.error("Revenue ($) is required for RevShare");
    if (isRS && (pct === "" || !Number.isFinite(Number(pct)))) return toast.error("Affiliate percentage (%) is required for RevShare");
    setBusy(true);
    try {
      const payload = {
        status,
        payout: isRS ? rsPayoutValue : (payout === "" ? undefined : Number(payout)),
        revenue: isRS ? Number(revenue) : (revenue === "" ? undefined : Number(revenue)),
        currency,
        send_postback: sendPostback,
        ...(bonus ? { bonus: true, buyer_id: buyerId, stream_id: streamId, quantity: qtyNum } : { click_ids: uniqueIds }),
      };
      const { data } = await http.post("/conversions/bulk-postback", payload);
      setResult(data); setPreview(null);
      qc.invalidateQueries({ queryKey: ["conversions"] });
      qc.invalidateQueries({ queryKey: ["clicks-server"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      if (bonus) toast.success("Bonus conversions created", { description: `${data.sent} click(s) + conversion(s) created` });
      else toast.success("Bulk postback sent", { description: `${data.sent} sent · ${data.not_found_count} not found` });
    } catch (e) { toast.error("Send failed", { description: e?.response?.data?.detail || "Try again." }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg border-white/[0.1] bg-[#141417]" data-testid="bulk-postback-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" /> Bulk Postback</DialogTitle>
          <DialogDescription>Mass-send conversions to a list of Click IDs. Preview first, then send.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {bonus ? (
            <div className="space-y-3" data-testid="bulk-postback-bonus-block">
              <div>
                <label className="mb-1.5 block text-sm text-foreground">Buyer</label>
                <SearchSelect value={buyerId} onChange={(v) => { setBuyerId(v); setStreamId(""); setResult(null); }}
                  options={buyerOpts} placeholder="Select buyer..." searchPlaceholder="Search buyer..."
                  testId="bulk-postback-bonus-buyer" widthClass="w-[300px]" triggerClass="h-9" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm text-foreground">Stream</label>
                  <SearchSelect value={streamId} onChange={(v) => { setStreamId(v); setResult(null); }}
                    options={streamOptions} disabled={!buyerId}
                    placeholder={buyerId ? "Select stream..." : "Select buyer first"} searchPlaceholder="Search stream..."
                    testId="bulk-postback-bonus-stream" widthClass="w-[300px]" triggerClass="h-9" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm text-foreground">Quantity</label>
                  <Input type="number" min={1} max={100} value={qty} onChange={(e) => { setQty(e.target.value); setResult(null); }}
                    placeholder="e.g. 5" className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-bonus-qty" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">The stream defines the offer. Each bonus conversion gets its own internal click (Click → Stream → Offer → Buyer).</p>
            </div>
          ) : (
          <div>
            <label className="mb-1.5 block text-sm text-foreground">Click IDs <span className="text-muted-foreground">(one per line)</span></label>
            <textarea value={text} onChange={(e) => { setText(e.target.value); setPreview(null); setResult(null); }}
              rows={6} placeholder={"clickid1\nclickid2\nclickid3"} data-testid="bulk-postback-ids"
              className="w-full rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3 font-mono text-xs text-foreground outline-none focus:border-white/25" />
            <p className="mt-1 text-xs text-muted-foreground">{uniqueIds.length} unique Click ID(s) {ids.length !== uniqueIds.length && `(${ids.length - uniqueIds.length} duplicate removed)`}</p>
          </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-sm text-foreground">Event template</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-event"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">{EVENTS.filter((e) => !bonus || e.value !== "trash").map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-foreground">Currency</label>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-currency" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-foreground">Revenue ($) {isRS ? <span className="text-xs text-[#F59E0B]">required</span> : <span className="text-muted-foreground text-xs">(optional)</span>}</label>
              <Input type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder={isRS ? "incoming revenue" : "from offer"} disabled={status === "trash" || status === "redep"} className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-revenue" />
            </div>
            {isRS ? (
              <>
                <div>
                  <label className="mb-1.5 block text-sm text-foreground">Affiliate percentage (%) <span className="text-xs text-[#F59E0B]">required</span></label>
                  <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="e.g. 30" className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-percent" />
                </div>
                <div className="col-span-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-2 text-sm" data-testid="bulk-postback-rs-payout">
                  <span className="text-muted-foreground">Payout (auto):</span>{" "}
                  <span className="font-mono font-semibold text-[#22C55E]">${(rsPayoutValue ?? 0).toFixed(2)}</span>
                  <span className="ml-2 text-xs text-muted-foreground">= Revenue × % / 100</span>
                </div>
              </>
            ) : (
              <div>
                <label className="mb-1.5 block text-sm text-foreground">Payout <span className="text-muted-foreground text-xs">(optional)</span></label>
                <Input type="number" value={payout} onChange={(e) => setPayout(e.target.value)} placeholder="from offer" disabled={status === "trash" || status === "redep"} className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-postback-payout" />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3">
            <label className="flex cursor-pointer select-none items-center justify-between gap-2 text-sm text-foreground">
              <span>Send Postback <span className="block text-xs text-muted-foreground">Fire the buyer's outbound S2S postbacks for these conversions</span></span>
              <Switch checked={sendPostback} onCheckedChange={setSendPostback} data-testid="bulk-postback-send-postback-toggle" />
            </label>
          </div>

          {preview && (
            <div className="rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3 text-sm" data-testid="bulk-postback-preview">
              <p className="text-foreground">Preview: <span className="text-[#22C55E] font-semibold">{preview.found}</span> found · <span className="text-[#EF4444] font-semibold">{preview.not_found_count}</span> not found of {preview.total}</p>
              {preview.not_found_count > 0 && <p className="mt-1 break-all font-mono text-xs text-muted-foreground">Not found: {preview.not_found.join(", ")}</p>}
            </div>
          )}
          {result && (
            <div className="rounded-lg border border-[#22C55E]/30 bg-[#22C55E]/5 p-3 text-sm" data-testid="bulk-postback-result">
              <p className="flex items-center gap-1.5 text-foreground"><CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> {result.bonus ? <>Created <span className="font-semibold text-[#22C55E]">{result.sent}</span> bonus conversion(s) with internal clicks.</> : <>Sent <span className="font-semibold text-[#22C55E]">{result.sent}</span> postback(s).</>}</p>
              {result.not_found_count > 0 && <p className="mt-1 flex items-center gap-1.5 text-muted-foreground"><XCircle className="h-4 w-4 text-[#EF4444]" /> {result.not_found_count} Click ID(s) not found: <span className="break-all font-mono text-xs">{result.not_found.join(", ")}</span></p>}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-foreground">
              <Switch checked={bonus} onCheckedChange={toggleBonus} data-testid="bulk-postback-bonus-toggle" />
              Bonus Conversion
            </label>
            <div className="flex gap-2">
              {!bonus && <Button variant="outline" disabled={busy} onClick={runPreview} data-testid="bulk-postback-preview-btn" className="gap-1.5 border-white/[0.12]"><Eye className="h-4 w-4" /> Preview</Button>}
              <Button disabled={busy || (bonus ? !(buyerId && streamId && qtyNum >= 1) : uniqueIds.length === 0)} onClick={send} data-testid="bulk-postback-send-btn" className="gap-1.5 bg-[#22C55E] text-black hover:bg-[#16a34a]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {bonus ? `Create ${qtyNum || ""}` : `Send ${uniqueIds.length || ""}`}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
